import React, { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Button } from "../ui/button";
import { ChevronRight, ArrowLeft, ImageOff } from "lucide-react";
import { ORDER_STATUSES } from "./utils";
import { useLocale } from "../../i18n/LocaleContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _resolve = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

function Thumb({ src, alt, size = 56, testid }) {
  const cls = "shrink-0 rounded-md object-cover bg-slate-50 border border-slate-200";
  if (src && (/^https?:\/\//i.test(src) || src.startsWith("/api/"))) {
    return (
      <img
        src={_resolve(src)}
        alt={alt}
        loading="lazy"
        data-testid={testid}
        className={cls}
        style={{ height: size, width: size }}
      />
    );
  }
  return (
    <div
      className={`${cls} flex items-center justify-center text-slate-400`}
      style={{ height: size, width: size }}
      data-testid={testid}
    >
      <ImageOff size={Math.round(size * 0.35)} strokeWidth={1.5} />
    </div>
  );
}

function OrderRowThumbStrip({ lines }) {
  // Stack up to 4 thumbs left-to-right, slight overlap. Adds a "+N" badge if more.
  const previews = (lines || []).slice(0, 4);
  const extra = (lines?.length || 0) - previews.length;
  return (
    <div className="flex items-center -space-x-2">
      {previews.map((l, i) => (
        <Thumb
          key={`${l.line_number}-${i}`}
          src={l.image_url}
          alt={l.description}
          size={32}
          testid={`order-row-thumb-${i}`}
        />
      ))}
      {extra > 0 && (
        <span className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-slate-100 border border-slate-200 text-[10px] font-bold text-slate-600">
          +{extra}
        </span>
      )}
    </div>
  );
}

const STATUS_TINT = {
  "Pending PO": "bg-slate-100 text-slate-700 border-slate-200",
  "Pending PO Acceptance": "bg-blue-50 text-blue-700 border-blue-200",
  "Pending Shipment": "bg-amber-50 text-amber-800 border-amber-200",
  Shipped: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Open: "bg-cyan-50 text-cyan-700 border-cyan-200",
  Closed: "bg-slate-100 text-slate-700 border-slate-200",
  "Pending Supplier Confirmation": "bg-amber-50 text-amber-800 border-amber-200",
};

const TYPE_TINT = {
  order: { label: "Catalog Order",   cls: "bg-[#0072CE]/10 text-[#0072CE] border-[#0072CE]/30" },
  rfq:   { label: "Instant RFQ",     cls: "bg-amber-50 text-amber-800 border-amber-200" },
  otp:   { label: "Tail of the Tail", cls: "bg-[#0B2A4A]/10 text-[#0B2A4A] border-[#0B2A4A]/25" },
};

function TypeBadge({ type }) {
  const cfg = TYPE_TINT[type] || TYPE_TINT.order;
  return (
    <span
      data-testid={`order-type-${type || "order"}`}
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] border ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const cls = STATUS_TINT[status] || STATUS_TINT["Pending PO"];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.12em] border ${cls}`}>
      {status}
    </span>
  );
}

export default function OrderHistory({ open, onOpenChange, orders, onOpenOrder, openedOrder, onCloseOrder, onRaiseSupport }) {
  const { t, fmt, fmtMoney } = useLocale();
  // openedOrder = full order object
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="orders-drawer">
        {!openedOrder ? (
          <>
            <SheetHeader>
              <SheetTitle className="font-display tracking-tight text-2xl">{t("orders.title")}</SheetTitle>
              <SheetDescription>Orders are created when you transfer your cart for PO creation. Status updates flow back from your ERP.</SheetDescription>
            </SheetHeader>

            <div className="mt-5">
              {!orders?.length && (
                <div className="text-sm text-slate-500 py-12 text-center">{t("orders.empty")}</div>
              )}

              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-12 px-4 py-3 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">
                  <div className="col-span-3">{t("orders.col_order")}</div>
                  <div className="col-span-2">{t("orders.col_cart")}</div>
                  <div className="col-span-2">{t("orders.col_items")}</div>
                  <div className="col-span-2">{t("orders.col_status")}</div>
                  <div className="col-span-2 text-right">{t("orders.col_total")}</div>
                  <div className="col-span-1" />
                </div>
                {orders?.map((o) => (
                  <button
                    key={o.order_id}
                    onClick={() => onOpenOrder(o.order_id)}
                    data-testid={`order-row-${o.order_id}`}
                    className="w-full grid grid-cols-12 px-4 py-4 items-center text-left border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <div className="col-span-3">
                      <div className="font-mono text-sm text-slate-900 font-semibold">{o.order_id}</div>
                      <div className="mt-1"><TypeBadge type={o.type} /></div>
                    </div>
                    <div className="col-span-2 font-mono text-sm text-slate-600">{o.cart_id}</div>
                    <div className="col-span-2"><OrderRowThumbStrip lines={o.lines} /></div>
                    <div className="col-span-2"><StatusBadge status={o.status} /></div>
                    <div className="col-span-2 text-right font-display font-semibold">
                      {o.type === "rfq"
                        ? <span className="text-slate-400">—</span>
                        : o.type === "otp"
                          ? <span>{o.currency} {Number(o.total_value || 0).toLocaleString()}</span>
                          : fmtMoney(o.total_value, { currency: o.currency, native: (o.currency && o.currency !== "EUR") })}
                    </div>
                    <div className="col-span-1 text-right text-slate-400"><ChevronRight size={16} /></div>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <SheetHeader>
              <button onClick={onCloseOrder} data-testid="order-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 self-start mb-2">
                <ArrowLeft size={14} /> Back to orders
              </button>
              <SheetTitle className="font-display tracking-tight text-2xl">{openedOrder.order_id}</SheetTitle>
              <SheetDescription>
                Cart <span className="font-mono">{openedOrder.cart_id}</span> · {openedOrder.lines.length} line item(s)
              </SheetDescription>
            </SheetHeader>

            <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 p-3">
              <StatusBadge status={openedOrder.status} />
              <div className="font-display text-xl font-bold text-slate-900" data-testid="order-detail-total">
                {fmtMoney(openedOrder.total_value, { currency: openedOrder.currency, native: (openedOrder.currency && openedOrder.currency !== "EUR") })}
              </div>
            </div>

            <div className="mt-5 space-y-3">
              {openedOrder.lines.map((l) => (
                <div key={l.line_number} data-testid={`order-line-${l.line_number}`} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-start gap-3">
                    <Thumb src={l.image_url} alt={l.description} size={72} testid={`order-line-thumb-${l.line_number}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Line {l.line_number} · {l.category}</div>
                      <div className="font-display text-base font-semibold text-slate-900 leading-tight mt-0.5">{l.description}</div>
                      <div className="text-xs text-slate-500 mt-1">{l.brand}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-slate-500">{fmtMoney(l.unit_price, { source_type: l.source_type, currency: l.currency })} × {l.quantity}</div>
                      <div className="font-display font-bold text-slate-900">{fmtMoney(l.line_total, { source_type: l.source_type, currency: l.currency })}</div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="bg-slate-50 border border-slate-100 rounded-md px-2.5 py-1.5">
                      <div className="text-[10px] uppercase text-slate-400 font-semibold">UniBuy™ PN</div>
                      <div className="font-mono font-semibold text-slate-900">{l.infoshop_pn}</div>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 rounded-md px-2.5 py-1.5">
                      <div className="text-[10px] uppercase text-slate-400 font-semibold">UNSPSC</div>
                      <div className="font-mono text-slate-900">{l.unspsc || "—"}</div>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 rounded-md px-2.5 py-1.5">
                      <div className="text-[10px] uppercase text-slate-400 font-semibold">UoM</div>
                      <div className="text-slate-900">{l.uom}</div>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 rounded-md px-2.5 py-1.5">
                      <div className="text-[10px] uppercase text-slate-400 font-semibold">Cart / Order</div>
                      <div className="font-mono text-slate-900 text-[10px]">{l.cart_id}<br />{l.order_id}</div>
                    </div>
                  </div>
                  <Button
                    onClick={() => onRaiseSupport({ order_id: openedOrder.order_id, infoshop_pn: l.infoshop_pn })}
                    data-testid={`order-line-support-${l.line_number}`}
                    variant="outline"
                    className="mt-3 text-xs"
                  >
                    Raise support for this line
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-md bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Status lifecycle</div>
              {openedOrder.type === "rfq" ? (
                <div className="text-slate-700">
                  <span className="font-semibold">Instant RFQ →</span> {openedOrder.destination || "Infosys Tactical Buying Desk"} ·
                  SLA {openedOrder.rfq_payload?.sla_business_days || 2} business days · status <strong>{openedOrder.status}</strong>
                </div>
              ) : openedOrder.type === "otp" ? (
                <div className="text-slate-700">
                  <span className="font-semibold">One-Time Purchase →</span> {openedOrder.destination || "ProPay Worldwide Inc."} ·
                  PO recipient · status <strong>{openedOrder.status}</strong>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  {ORDER_STATUSES.map((s, i) => (
                    <React.Fragment key={s}>
                      <span className={`px-2 py-1 rounded text-[11px] ${s === openedOrder.status ? "bg-[#0072CE] text-white font-semibold" : "bg-white border border-slate-200 text-slate-600"}`}>{s}</span>
                      {i < ORDER_STATUSES.length - 1 && <ChevronRight size={12} className="text-slate-400" />}
                    </React.Fragment>
                  ))}
                </div>
              )}
              {openedOrder.documented_actions?.length > 0 && (
                <ul className="mt-4 space-y-1.5">
                  {openedOrder.documented_actions.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px] text-slate-600">
                      <span className="mt-1 h-1 w-1 rounded-full bg-slate-400 shrink-0" />
                      <span className="leading-relaxed">{a}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
